import React from "react";
import { Add, InfoOutlined, PrintOutlined } from "@mui/icons-material";
import { Box, Button, Chip, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { useLazyGetDeliveryCollectionListQuery, useLazyListDeliveryCollectionBillingPrintQuery } from "store/services/deliveryCollectionApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";

import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { handlePrint } from "./DeliveryCollectionComponents/DeliveryCollectionBillingPrint";

const DeliveryCollectionListScreen = () => {

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { viewEntity } = useAuth();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Delivery Collections" }];

  const deliveryCollectionTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  // State for filters
  const [user, setUser] = React.useState("");

  const [collectStatus, isCollectStatus] = React.useState("");

  const [fetchDeliveryCollectionPrint] = useLazyListDeliveryCollectionBillingPrintQuery();

  const columns = [
    {
      name: "Delivery Collection ID",
      selector: (row) => row?.deliveryCollectionId,
      cell: (row) => {
        const displayId = row?.deliveryCollectionId;
        const path = `/app/crm/delivery-collection/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Delivery Type",
      selector: (row) =>
        row?.responsibleUser
          ? "User Delivery"
          : row?.driver || row?.contractor
            ? "Driver Delivery"
            : "N/A",
      cell: (row) => (
        <div>
          {row?.responsibleUser
            ? "User Delivery"
            : row?.driver || row?.contractor
              ? "Driver Delivery"
              : "N/A"}
        </div>
      ),
    },
    {
      name: "Actual Amount",
      selector: (row) => row?.actualAmount,
      cell: (row) => row?.actualAmount?.toFixed(2) || "0.00",
    },
    {
      name: "Collected Amount",
      selector: (row) => row?.collectedAmount,
      cell: (row) => row?.collectedAmount?.toFixed(2) || "0.00",
    },
    {
      name: "Collection Mode",
      selector: (row) => row?.collectionMode,
      cell: (row) => (
        <div title={row?.collectionMode?.name} className="line-clamp-2">
          {row?.collectionMode?.name || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Remittance Remarks",
      selector: (row) => row?.remittanceRemarks,
      cell: (row) => row?.remittanceRemarks || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row.createdAt} />,
    },
    {
      name: "Clearance Status",
      selector: (row) => row?.collectStatus || "Pending",
      cell: (row) => {
        const status = row?.collectStatus || "Pending";

        const getColor = (status) => {
          switch (status) {
            case "Approved":
              return "success";
            case "Rejected":
              return "error";
            case "Pending":
            default:
              return "warning";
          }
        };

        return (
          <Chip
            label={status}
            color={getColor(status)}
            title={status}
            size="small"
            sx={{
              minWidth: "70px",
              height: 20,
              fontSize: "0.7rem",
              borderRadius: "25px",
            }}
          />
        );
      },
      minWidth: "200px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/delivery-collection/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Delivery Collection"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <IconButton
            size="small"
            aria-label="Print COC Collection"
            onClick={() => handlePrint(row?._id, fetchDeliveryCollectionPrint)}
          >
            <PrintOutlined color="primary" />
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchDeliveryCollections,
    { data: deliveryCollectionRes, isLoading, isFetching, error },
  ] = useLazyGetDeliveryCollectionListQuery();
  // Fetch users

  const [fetchUsers, { data: usersData, isLoading: loadingUsers }] =
    useLazyGetDDLUserListQuery();

  React.useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      fetchUsers({
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
      });
    }
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    fetchUsers,
  ]);

  React.useEffect(() => {
    fetchDeliveryCollections({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(collectStatus && { collectStatus }),
      ...(user && { addedUser: user }),
    });
  }, [activeTab, collectStatus, currentPage, fetchDeliveryCollections, keyword, perPage, user]);

  useAppMeta(`${activeTab} Delivery Collections`);

  // prepare for data in dropdown
  const userOptions = React.useMemo(() => {
    const validUsers = Array.isArray(usersData)
      ? usersData.map((item) => ({
        label: item?.name,
        value: item?._id,
      }))
      : [];
    return [{ label: "All Users", value: "" }, ...validUsers];
  }, [usersData]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={deliveryCollectionTabs} activeTab={activeTab} />
        <TableFilter
          searchLabel="Delivery Collection Id"
          disabled={isLoading || isFetching} initialFilters={3}
        >
          <BaseAutocomplete
            fullWidth
            label="Requested By"
            value={userOptions.find((opt) => opt.value === user) || null}
            options={userOptions}
            onChange={(_, selected) => {
              setUser(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown User"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingUsers}
          />
          <BaseSelect
            fullWidth
            label="Clearance Status"
            value={collectStatus}
            onChange={(e) => isCollectStatus(e.target.value)}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Pending", value: "Pending" },
              { label: "Approved", value: "Approved" },
              { label: "Rejected", value: "Rejected" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={deliveryCollectionRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={deliveryCollectionRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DeliveryCollectionListScreen;

const PageActions = () => {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Link to="/app/crm/delivery-collection/createByShipment">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Delivery Collection By Shipment
        </Button>
      </Link>
      <Link to="/app/crm/delivery-collection/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Delivery Collection By User
        </Button>
      </Link>
    </Box>
  );
};
